import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Subject, takeUntil, Observable, switchMap, map, tap, catchError, of, throwError, take } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { AlertService } from '@selfai-platform/shared';
import { AgentService } from './agent.service';
import { SchemaExtractionService } from './schema-extraction.service';
import { ThreadManagementService } from './thread-management.service';
import {
  Agent,
  AgentType,
  AgentContext,
  AgentExecutionRequest,
  ChatMessage,
  ChatThread,
} from '../models';

@Injectable({
  providedIn: 'root'
})
export class ChatService implements OnDestroy {
  private readonly messagesSubject = new BehaviorSubject<ChatMessage[]>([]);
  private readonly isProcessingSubject = new BehaviorSubject<boolean>(false);
  private readonly destroy$ = new Subject<void>();
  private readonly selectedAgentSubject = new BehaviorSubject<Agent | null>(null);
  private readonly currentThreadSubject = new BehaviorSubject<ChatThread | null>(null);
  private loadingHistoryForThread: string | null = null;

  private currentContext: AgentContext = {
    userId: 'default-user'
  };

  readonly contextSubject: BehaviorSubject<string> = new BehaviorSubject<string>('');

  public messages$ = this.messagesSubject.asObservable();
  public isProcessing$ = this.isProcessingSubject.asObservable();
  public selectedAgent$ = this.selectedAgentSubject.asObservable();

  constructor(
    private agentService: AgentService,
    private schemaExtractionService: SchemaExtractionService,
    private threadManagementService: ThreadManagementService,
    private translateService: TranslateService,
    private readonly alertService: AlertService,
  ) {
    this.initializeListeners();
    this.subscribeToActiveThread();

    this.threadManagementService.loadAllUserThreads().pipe(takeUntil(this.destroy$)).subscribe();
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public setContext(context: string): Observable<void> {
    this.contextSubject.next(context);

    if (!context || context.trim() === '') {
      this.clearCurrentContext();
      return of(void 0);
    }

    try {
      const contextData = JSON.parse(context);
      this.updateCurrentContext(contextData);
    } catch (error) {
      console.warn('Failed to parse context data:', error);
      this.clearCurrentContext();
    }

    return of(void 0);
  }

  public sendMessage(content: string, agentType?: AgentType, data?: any): Observable<void> {
    if (!content.trim()) return of(void 0);

    const selectedAgent = this.selectedAgentSubject.value;
    if (!selectedAgent) {
      return throwError(() => new Error(this.translateService.instant('chat.errors.no-agent-selected')));
    }

    const currentThread = this.threadManagementService.getActiveThread();

    if (!currentThread) {
      return throwError(() => new Error(this.translateService.instant('chat.errors.no-active-thread')));
    }

    const isTemporary = this.threadManagementService.isTemporaryThread(currentThread.id);

    const userMessage: ChatMessage = {
      id: this.generateId(),
      content,
      timestamp: new Date(),
      isUser: true,
      agentType: selectedAgent.type,
      data,
    };

    const currentMessages = this.messagesSubject.value;
    this.messagesSubject.next([...currentMessages, userMessage]);
    this.threadManagementService.addMessageToThread(currentThread.id, userMessage);

    this.isProcessingSubject.next(true);

    const threadOperation$ = isTemporary
      ? this.threadManagementService.createActualThread(currentThread.id, selectedAgent.type, content)
      : of(currentThread);

    return threadOperation$.pipe(
      switchMap((actualThread) => {
        const executionRequest: AgentExecutionRequest = {
          agentType: selectedAgent.type,
          query: content,
          context: this.currentContext,
          threadId: actualThread.agentThreadId || actualThread.id,
          saveHistory: true,
          generateTitle: true,
        };

        return this.agentService.executeAgent(executionRequest).pipe(
          switchMap((response) => {
            const content = response.content || '';

            const assistantMessage: ChatMessage = {
              id: this.generateId(),
              content: content,
              timestamp: new Date(),
              isUser: false,
              agentType: selectedAgent.type,
              threadId: response.threadId,
              executionId: response.executionId,
              metadata: {
                codeBlock: this.extractCodeBlock(content),
                hasError: !response.success,
                errorMessage: response.error?.message,
                cubeId: this.currentContext.cubeId,
                workflowId: this.currentContext.workflowId,
                plan: response.metadata?.plan,
                debug: response.metadata?.['debug'],
                errors: response.metadata?.['errors'],
                warnings: response.metadata?.['warnings'],
                suggestions: response.metadata?.['suggestions'],
              },
            };

            const updatedMessages = [...this.messagesSubject.value, assistantMessage];
            this.messagesSubject.next(updatedMessages);

            this.threadManagementService.addMessageToThread(actualThread.id, assistantMessage);

            if (response.result?.thread_title) {
              this.threadManagementService.updateThreadWithGeneratedTitle(actualThread.id, response.result.thread_title);
            }

            this.isProcessingSubject.next(false);
            return of(void 0);
          }),
          catchError((error) => {
            this.alertService.error(this.translateService.instant('chat.errors.agent-execution-failed-simple') + ': ' + (error.message || error));

            const errorMessage: ChatMessage = {
              id: this.generateId(),
              content: `Error: ${error.message || this.translateService.instant('chat.errors.agent-execution-failed')}`,
              timestamp: new Date(),
              isUser: false,
              agentType: selectedAgent.type,
              metadata: {
                hasError: true,
                errorMessage: error.message,
              },
            };

            const updatedMessages = [...this.messagesSubject.value, errorMessage];
            this.messagesSubject.next(updatedMessages);

            this.threadManagementService.addMessageToThread(actualThread.id, errorMessage);
            this.isProcessingSubject.next(false);

            return of(void 0);
          })
        );
      }),
      catchError((error) => {
        this.alertService.error(this.translateService.instant('chat.errors.error-sending-message') + ': ' + (error.message || error));
        this.isProcessingSubject.next(false);
        return of(void 0);
      })
    );
  }

  private clearCurrentContext(): void {
    this.currentContext = {
      userId: 'default-user'
    };
  }

  private updateCurrentContext(contextData: any): void {
    this.schemaExtractionService.createEnhancedContext(contextData).pipe(
      tap((enhancedContext) => {
        this.currentContext = {
          ...this.currentContext,
          serializedData: contextData,
          workflowId: enhancedContext.workflowId,
          cubeId: enhancedContext.nodeId,
          originalCubeId: enhancedContext.originalNodeId,
          cubeName: contextData?.operation?.name || contextData?.name,
          inputNodeIds: enhancedContext.inputNodeIds || [],
          schema: enhancedContext.schema || [],
          nodeData: enhancedContext,
        };
      }),
      catchError((error) => {
        console.warn('Failed to create enhanced context, using basic context:', error);

        const inputNodeIds = this.schemaExtractionService.extractInputNodeIds(contextData);
        this.currentContext = {
          ...this.currentContext,
          serializedData: contextData,
          workflowId: contextData?.workflowId || contextData?.workflow?.id,
          cubeId: contextData?.operation?.id || contextData?.id,
          originalCubeId: contextData?.operation?.id || contextData?.id,
          cubeName: contextData?.operation?.name || contextData?.name,
          inputNodeIds: inputNodeIds || [],
          schema: this.schemaExtractionService.extractSchemaFromContext(contextData) || [],
        };

        return of(void 0);
      })
    ).subscribe();
  }

  private extractCodeBlock(content: string): string | undefined {
    if (!content) return undefined;

    const codeBlockRegex = /```[\w]*\n([\s\S]*?)\n```/;
    const match = content.match(codeBlockRegex);
    return match ? match[1] : undefined;
  }

  private initializeListeners(): void {
    this.agentService.agentHealth$.pipe(
      takeUntil(this.destroy$)
    ).subscribe((isHealthy) => {
      if (!isHealthy) {
        this.selectedAgentSubject.next(null);
      }
    });
  }

  private subscribeToActiveThread(): void {
    this.threadManagementService.activeThread$.pipe(takeUntil(this.destroy$)).subscribe((thread) => {
      if (thread) {
        this.currentThreadSubject.next(thread);
        this.messagesSubject.next(thread.messages);

        if (thread.messages.length === 0 && thread.id && this.loadingHistoryForThread !== thread.id) {
          this.loadingHistoryForThread = thread.id;
          this.threadManagementService.loadThreadHistory(thread.id).pipe(
            takeUntil(this.destroy$),
            tap(() => {
              this.loadingHistoryForThread = null;
            }),
            catchError((error) => {
              console.warn('Failed to load thread history:', error);
              this.loadingHistoryForThread = null;
              return of([]);
            })
          ).subscribe();
        }

        if (thread.metadata?.context) {
          this.contextSubject.next(JSON.stringify(thread.metadata.context));
        }

        if (thread.agentType) {
          this.agentService.availableAgents$.pipe(
            map(agents => agents.find(agent => agent.type === thread.agentType)),
            tap(agent => {
              if (agent) {
                this.selectedAgentSubject.next(agent);
              }
            }),
            take(1)
          ).subscribe();
        }
      } else {
        this.currentThreadSubject.next(null);
        this.messagesSubject.next([]);
        this.contextSubject.next('');
        this.selectedAgentSubject.next(null);
        this.clearCurrentContext();
      }
    });
  }

  private generateId(): string {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }
}
